import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  UnfoldMore as UnfoldIcon,
  GraphicEq as AudioIcon,
  LibraryBooks as LibraryIcon,
  Schedule as ScheduleIcon,
  VerifiedUser as VerifiedIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import TranscricaoPainel from './TranscricaoPainel';
import {
  JobTranscricao,
  ArquivoTranscricao,
  FiltrosPainel,
  PastaTranscrita,
  ResumoTranscricoes,
  formatarDuracao,
  formatarDuracaoLonga,
  formatarPercentual,
} from '../services/transcricao';
import { colors } from '../theme/expertiseTheme';

/**
 * Resultado de uma transcrição concluída, com painel de indicadores e
 * histórico de pastas transcritas
 */
type Props = {
  job: JobTranscricao;
  onBaixarArquivo: (arquivo: ArquivoTranscricao) => void;
  onExportarLote: () => void;
  onNovaTranscricao: () => void;
  baixando: boolean;
  filtrosPainel: FiltrosPainel;
  onFiltrosPainelChange: (filtros: FiltrosPainel) => void;
  projetos: string[];
  resumo: ResumoTranscricoes | null;
  historico: PastaTranscrita[];
  loadingPainel: boolean;
};

const LIMITE_PREVIA = 320;

const TranscricaoResultados: React.FC<Props> = ({
  job,
  onBaixarArquivo,
  onExportarLote,
  onNovaTranscricao,
  baixando,
  filtrosPainel,
  onFiltrosPainelChange,
  projetos,
  resumo,
  historico,
  loadingPainel,
}) => {
  const [busca, setBusca] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);
  const [arquivoVisualizado, setArquivoVisualizado] = useState<ArquivoTranscricao | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  const arquivos = useMemo(() => job.arquivos || [], [job.arquivos]);

  const duracaoTotal = useMemo(
    () => arquivos.reduce((total, arquivo) => total + (arquivo.duracaoSegundos || 0), 0),
    [arquivos]
  );

  const confiancaMedia = useMemo(() => {
    if (job.confiancaMedia || job.confiancaMedia === 0) return job.confiancaMedia;
    const comConfianca = arquivos.filter((arquivo) => typeof arquivo.confiancaIa === 'number');
    if (!comConfianca.length) return undefined;
    return (
      comConfianca.reduce((total, arquivo) => total + (arquivo.confiancaIa || 0), 0) /
      comConfianca.length
    );
  }, [arquivos, job.confiancaMedia]);

  const arquivosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return arquivos;
    return arquivos.filter(
      (arquivo) =>
        arquivo.nome.toLowerCase().includes(termo) ||
        (arquivo.transcricao || '').toLowerCase().includes(termo)
    );
  }, [arquivos, busca]);

  const indicadores = [
    {
      titulo: 'Total de Arquivos',
      valor: String(job.totalArquivos ?? arquivos.length),
      icon: <LibraryIcon sx={{ fontSize: 24 }} />,
      cor: '#7C3AED',
      bgColor: 'rgba(124, 58, 237, 0.1)',
    },
    {
      titulo: 'Duração Total',
      valor: formatarDuracaoLonga(duracaoTotal),
      icon: <ScheduleIcon sx={{ fontSize: 24 }} />,
      cor: colors.info,
      bgColor: 'rgba(59, 130, 246, 0.1)',
    },
    {
      titulo: 'Confiança Média IA',
      valor: formatarPercentual(confiancaMedia),
      icon: <VerifiedIcon sx={{ fontSize: 24 }} />,
      cor: colors.primary,
      bgColor: 'rgba(0, 99, 0, 0.1)',
    },
  ];

  const handleCopiar = (arquivo: ArquivoTranscricao) => {
    navigator.clipboard
      ?.writeText(arquivo.transcricao || '')
      .then(() => {
        setCopiado(arquivo.id);
        setTimeout(() => setCopiado(null), 2000);
      })
      .catch(() => {});
  };

  const previa = (arquivo: ArquivoTranscricao): string => {
    if (arquivo.status !== 'Concluido') return 'Este arquivo não foi transcrito.';
    const texto = arquivo.transcricao || '';
    if (!texto) return 'A transcrição completa está disponível no download do arquivo.';
    return texto.length > LIMITE_PREVIA ? `${texto.slice(0, LIMITE_PREVIA)}...` : texto;
  };

  return (
    <Box>
      {/* Indicadores do lote */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {indicadores.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.titulo}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: 3,
                border: `1px solid ${colors.border}`,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  backgroundColor: item.bgColor,
                  color: item.cor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: colors.text.secondary,
                  }}
                >
                  {item.titulo}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: colors.dark, lineHeight: 1.2 }}
                >
                  {item.valor}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Busca e exportação */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar termo ou arquivo..."
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          sx={{ width: { xs: '100%', md: 380 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.text.light, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onNovaTranscricao}
          >
            Nova transcrição
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={onExportarLote}
            disabled={baixando || job.arquivosConcluidos === 0}
          >
            Exportar Tudo (Lote)
          </Button>
        </Box>
      </Box>

      {/* Painel de indicadores e histórico */}
      <TranscricaoPainel
        filtros={filtrosPainel}
        onChange={onFiltrosPainelChange}
        projetos={projetos}
        resumo={resumo}
        historico={historico}
        loading={loadingPainel}
      />

      {/* Transcrições do lote */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 0.5 }}>
          Transcrições do lote
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
          {arquivosFiltrados.length} de {arquivos.length} arquivo
          {arquivos.length !== 1 ? 's' : ''}
        </Typography>

        {arquivosFiltrados.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {arquivos.length === 0
                ? 'Nenhum arquivo de áudio foi processado neste lote.'
                : `Nenhum arquivo encontrado para "${busca}".`}
            </Typography>
          </Paper>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {arquivosFiltrados.map((arquivo) => {
            const aberto = expandido === arquivo.id;
            const concluido = arquivo.status === 'Concluido';

            return (
              <Paper
                key={arquivo.id}
                elevation={0}
                sx={{
                  border: `1px solid ${aberto ? colors.primary : colors.border}`,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s ease',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    p: 2.5,
                    backgroundColor: aberto ? 'rgba(0, 99, 0, 0.04)' : colors.white,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandido(aberto ? null : arquivo.id)}
                      sx={{ color: colors.text.secondary }}
                    >
                      <UnfoldIcon fontSize="small" />
                    </IconButton>

                    <AudioIcon sx={{ fontSize: 22, color: colors.text.light, flexShrink: 0 }} />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: colors.dark,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {arquivo.nome}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                        {formatarDuracao(arquivo.duracaoSegundos)}
                        {typeof arquivo.confiancaIa === 'number' && (
                          <>
                            {'  •  '}Confiança da IA:{' '}
                            <Box component="strong" sx={{ color: colors.primary }}>
                              {formatarPercentual(arquivo.confiancaIa)}
                            </Box>
                          </>
                        )}
                        {arquivo.erro && (
                          <Box component="span" sx={{ color: colors.error }}>
                            {'  •  '}
                            {arquivo.erro}
                          </Box>
                        )}
                        {(arquivo.status === 'Pendente' || arquivo.status === 'Processando') && (
                          <Box component="span">{'  •  '}Na fila (não transcrito)</Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => onBaixarArquivo(arquivo)}
                      disabled={!concluido || baixando}
                    >
                      Baixar TXT
                    </Button>

                    <Tooltip title={copiado === arquivo.id ? 'Copiado!' : 'Copiar transcrição'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<CopyIcon sx={{ fontSize: 18 }} />}
                          onClick={() => handleCopiar(arquivo)}
                          disabled={!arquivo.transcricao}
                        >
                          {copiado === arquivo.id ? 'Copiado' : 'Copiar'}
                        </Button>
                      </span>
                    </Tooltip>

                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setArquivoVisualizado(arquivo)}
                      disabled={!concluido}
                    >
                      Ver Completo
                    </Button>
                  </Box>
                </Box>

                <Collapse in={aberto} unmountOnExit>
                  <Box
                    sx={{
                      px: 3,
                      py: 2.5,
                      borderTop: `1px solid ${colors.border}`,
                      backgroundColor: colors.light,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: colors.text.secondary,
                        mb: 1.5,
                      }}
                    >
                      Pré-visualização da transcrição
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.text.primary,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.8,
                      }}
                    >
                      {previa(arquivo)}
                    </Typography>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Transcrição completa */}
      <Dialog
        open={Boolean(arquivoVisualizado)}
        onClose={() => setArquivoVisualizado(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: colors.dark }}>
          {arquivoVisualizado?.nome}
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ color: colors.text.primary, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
          >
            {arquivoVisualizado?.transcricao ||
              'A transcrição completa está disponível no download do arquivo.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            color="primary"
            startIcon={<CopyIcon />}
            onClick={() => arquivoVisualizado && handleCopiar(arquivoVisualizado)}
            disabled={!arquivoVisualizado?.transcricao}
          >
            Copiar texto
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => arquivoVisualizado && onBaixarArquivo(arquivoVisualizado)}
            disabled={baixando}
          >
            Baixar .txt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranscricaoResultados;
